import { useCallback, useRef } from 'react';
import { useDispatch } from 'react-redux';

import { type GetTaskResponse, type TaskType as RemoteTaskType } from '@air/api/types';

import { useEventSubscription } from '~/hooks/useEventSubscription';
import {
  markTaskAsCompletedAction,
  markTaskAsFailedAction,
  removeTasksAction,
  setTaskAction,
  updateTaskMetadataAction,
} from '~/store/tasks/actions';
import {
  type GetCompletedMetadata,
  type GetInProgressMetadata,
  type GetTaskByType,
  type TaskType as LocalTaskType,
  type TaskType,
} from '~/store/tasks/types';
import { type AirState } from '~/store/types';
import { reportErrorToBugsnag } from '~/utils/ErrorUtils';
import { useAirStore } from '~/utils/ReduxUtils';
import { getTasksFromLocalStorage, removeTasksFromLocalStorage, updateTaskInLocalStorage } from './storage';

type TasksSelector<T extends TaskType> = ({ tasks }: AirState) => GetTaskByType<T>[];

type GetTaskResponseByType<T extends RemoteTaskType> = Extract<GetTaskResponse, { type: T }>;

type NormalizedTaskFromServer<T extends RemoteTaskType> = Pick<
  GetTaskResponseByType<T>,
  'type' | 'id' | 'attempts' | 'completedAt'
> & {
  data?: GetTaskResponseByType<T>['data'];
  args?: GetTaskResponseByType<T>['args'];
};

export type SharedSyncTasksParams<LocalType extends LocalTaskType, RemoteType extends RemoteTaskType> = {
  localType: LocalType;
  remoteType: RemoteType;
  onComplete?: (params: {
    task: NormalizedTaskFromServer<RemoteType>;
    localTask: GetTaskByType<LocalType>;
    enrichMetadata: (metadata: Partial<GetCompletedMetadata<GetTaskByType<LocalType>, false>>) => void;
  }) => Promise<void> | void;
  onUpdate?: (params: {
    task: NormalizedTaskFromServer<RemoteType>;
    localTask: GetTaskByType<LocalType>;
    enrichMetadata: (metadata: Partial<GetInProgressMetadata<GetTaskByType<LocalType>>>) => void;
  }) => Promise<void> | void;
  onError?: (params: { task: NormalizedTaskFromServer<RemoteType>; localTask: GetTaskByType<LocalType> }) => void;
  tasksSelector: TasksSelector<LocalType>;
  getTask: (taskId: string) => Promise<GetTaskResponse>;
  workspaceId?: string;
};

export const useSharedSyncTasks = <
  LocalType extends LocalTaskType = LocalTaskType,
  RemoteType extends RemoteTaskType = RemoteTaskType,
>({
  localType,
  remoteType,
  tasksSelector,
  getTask,
  workspaceId,
  onComplete,
  onUpdate,
  onError,
}: SharedSyncTasksParams<LocalType, RemoteType>) => {
  type CurrentTaskType = GetTaskByType<LocalType>;

  const dispatch = useDispatch();
  const store = useAirStore();

  const onCompleteRef = useRef(onComplete);
  onCompleteRef.current = onComplete;

  const onUpdateRef = useRef(onUpdate);
  onUpdateRef.current = onUpdate;

  const onErrorRef = useRef(onError);
  onErrorRef.current = onError;

  const syncLocalTasks = useCallback(() => {
    if (!workspaceId) {
      return;
    }

    const tasks = tasksSelector(store.getState());

    tasks.forEach((localTask) => {
      const { localTaskId, remoteTaskId } = localTask;
      if (!remoteTaskId) {
        return;
      }

      getTask(remoteTaskId)
        .then(async (remoteTask) => {
          if (remoteTask.type !== remoteType) {
            return;
          }

          dispatch(
            setTaskAction({
              ...localTask,
              ...(localTask.status === 'pending' && remoteTask.status === 'active' ? { status: 'in-progress' } : {}),
              startedAt: remoteTask.startedAt,
              createdAt: remoteTask.createdAt,
              updatedAt: remoteTask.updatedAt,
              metadata: {
                ...localTask.metadata,
                ...remoteTask.data,
              },
            }),
          );

          switch (remoteTask.status) {
            case 'exception':
            case 'failed':
              dispatch(
                markTaskAsFailedAction({
                  localTaskId,
                }),
              );
              onErrorRef.current?.({
                task: remoteTask as unknown as NormalizedTaskFromServer<RemoteType>,
                localTask,
              });
              break;

            case 'succeeded': {
              const enricher = makeTaskMetadataEnricher<GetCompletedMetadata<CurrentTaskType, false>>();

              await onCompleteRef.current?.({
                task: remoteTask as unknown as NormalizedTaskFromServer<RemoteType>,
                localTask,
                enrichMetadata: enricher.set,
              });
              removeTasksFromLocalStorage([localTaskId]);

              dispatch(
                markTaskAsCompletedAction({
                  localTaskId,
                  metadata: enricher.get() ?? {},
                }),
              );
              break;
            }

            case 'cancelled': {
              removeTasksFromLocalStorage([localTaskId]);
              dispatch(removeTasksAction({ taskIds: [localTaskId] }));
              break;
            }
          }
        })
        .catch((error) => {
          reportErrorToBugsnag({
            error,
            context: 'Failed to get task useSharedSyncTasks',
          });
        });
    });
  }, [dispatch, getTask, remoteType, store, tasksSelector, workspaceId]);

  const loadFromStorage = useCallback(() => {
    const tasks = getTasksFromLocalStorage().filter((task) => task.type === localType);
    tasks.forEach((localStorageTask) => {
      dispatch(setTaskAction(localStorageTask));
    });
    syncLocalTasks();
  }, [dispatch, syncLocalTasks, localType]);

  // Task initially has pending status, so we need to subscribe to the started event to update the task status to in-progress
  const { isSubscribed: isStartedSubscriptionReady } = useEventSubscription({
    currentWorkspaceId: workspaceId,
    event: {
      eventType: 'System.Task.started',
      pattern: {
        type: remoteType,
      },
    },
    options: {
      onUpdate: async (message) => {
        const taskId = message.data.id;
        const tasks = tasksSelector(store.getState());

        const localTask = tasks.find(
          (taskCandidate): taskCandidate is CurrentTaskType => taskCandidate.remoteTaskId === taskId,
        );

        if (!localTask) {
          return;
        }

        const enricher = makeTaskMetadataEnricher<GetInProgressMetadata<CurrentTaskType>>();

        await onUpdateRef.current?.({
          task: message.data as unknown as NormalizedTaskFromServer<RemoteType>,
          localTask,
          enrichMetadata: enricher.set,
        });

        updateTaskInLocalStorage<CurrentTaskType>({
          ...localTask,
          status: 'in-progress',
        });

        dispatch(
          setTaskAction({
            ...localTask,
            status: 'in-progress',
            startedAt: message.data.startedAt,
          }),
        );
      },
    },
  });

  const { isSubscribed: isUpdateSubscriptionReady } = useEventSubscription({
    currentWorkspaceId: workspaceId,
    event: {
      eventType: 'System.Task.updated',
      pattern: {
        type: remoteType,
      },
    },
    options: {
      onUpdate: async (message) => {
        const taskId = message.data.id;
        const tasks = tasksSelector(store.getState());

        const localTask = tasks.find(
          (taskCandidate): taskCandidate is CurrentTaskType => taskCandidate.remoteTaskId === taskId,
        );

        if (!localTask) {
          return;
        }

        const enricher = makeTaskMetadataEnricher<GetInProgressMetadata<CurrentTaskType>>();

        await onUpdateRef.current?.({
          task: message.data as unknown as NormalizedTaskFromServer<RemoteType>,
          localTask,
          enrichMetadata: enricher.set,
        });

        const metadata =
          message.data.data || enricher.get()
            ? {
                ...message.data.data,
                ...enricher.get(),
              }
            : undefined;

        if (metadata) {
          // Typescript error is suppressed because the task type is a union of every possible task type along with every asset transformation subtype
          // @ts-expect-error: Expression produces a union type that is too complex to represent.
          updateTaskInLocalStorage<CurrentTaskType>({
            ...localTask,
            status: 'in-progress',
            metadata: {
              ...localTask.metadata,
              ...message.data.data,
              ...metadata,
            },
          });

          dispatch(
            updateTaskMetadataAction({
              localTaskId: localTask.localTaskId,
              metadata,
            }),
          );
        }
      },
    },
  });

  const { isSubscribed: isCompletedSubscriptionReady } = useEventSubscription({
    currentWorkspaceId: workspaceId,
    event: {
      eventType: 'System.Task.completed',
      pattern: {
        type: remoteType,
      },
    },
    options: {
      onUpdate: async (message) => {
        const tasks = tasksSelector(store.getState());

        const remoteTask = message.data as unknown as NormalizedTaskFromServer<RemoteType>;

        const localTask = tasks.find(({ remoteTaskId }) => remoteTaskId === message.data.id);

        if (localTask) {
          const { localTaskId } = localTask;

          removeTasksFromLocalStorage([localTaskId]);

          if (message.data.status === 'failed' || message.data.status === 'exception') {
            dispatch(
              markTaskAsFailedAction({
                localTaskId,
              }),
            );
            onErrorRef.current?.({ task: remoteTask, localTask });
          } else {
            const enricher = makeTaskMetadataEnricher<GetCompletedMetadata<CurrentTaskType, false>>();
            await onCompleteRef.current?.({
              task: remoteTask,
              localTask,
              enrichMetadata: enricher.set,
            });

            const metadata = {
              ...remoteTask.data,
              ...enricher.get(),
            };

            dispatch(
              markTaskAsCompletedAction({
                localTaskId: localTask.localTaskId,
                metadata,
              }),
            );
          }
        }
      },
    },
  });

  // All task-related subscriptions must be ready for safe task syncing
  const subscriptionsReady = isStartedSubscriptionReady && isUpdateSubscriptionReady && isCompletedSubscriptionReady;

  return { syncLocalTasks, loadFromStorage, subscriptionsReady };
};

function makeTaskMetadataEnricher<T>() {
  const value = {
    current: undefined as Partial<T> | undefined,
  };

  return {
    set: (metadata: Partial<T>) => {
      value.current = metadata;
    },
    get: () => value.current,
  };
}
